import React, { useState } from "react";
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import PerimeterModalFooter from "components/modal/dataModal/data/perimeter/PerimeterModalFooter";
import usePerimeterModal from "components/modal/dataModal/data/perimeter/usePerimeterModal";
import { WarningCircle } from "assets/icons";
import PerimeterMemberPicker from "pages/profil/perimeters/PerimeterMemberPicker";
import { haveSameMemberIds } from "pages/profil/perimeters/perimeterMembers";
import { fillText } from "pages/profil/perimeters/perimeterText";

const NAME_MAX_LENGTH = 60;

const PerimeterFormModal = ({ mode = "create" }) => {
    const isEdit = mode === "edit";
    const { language, perimeterModalData } = UseContext();
    const { isSaving, setIsSaving, close, succeed, fail } = usePerimeterModal();
    const perimeter = perimeterModalData?.perimeter;
    const assignableMembers = perimeterModalData?.assignableMembers || [];
    // What the form started with, to tell whether an edit changed anything.
    const [initial] = useState(() => ({
        name: isEdit ? perimeter?.name || "" : "",
        memberIds: isEdit
            ? (perimeter?.members || []).map((member) => member.id)
            : [],
    }));
    const [name, setName] = useState(initial.name);
    const [memberIds, setMemberIds] = useState(initial.memberIds);
    const [nameError, setNameError] = useState("");

    const trimmedName = name.trim();
    const isNameChanged = trimmedName !== initial.name;
    const areMembersChanged = !haveSameMemberIds(memberIds, initial.memberIds);
    // The rule is on the trimmed name, so a pasted name with spaces around it
    // is not cut by the input.
    const isNameTooLong = trimmedName.length > NAME_MAX_LENGTH;
    const displayedNameError = isNameTooLong
        ? language.getValue("perimeters.errors.nameTooLong")
        : nameError;
    const canSubmit =
        trimmedName !== "" &&
        !isNameTooLong &&
        (!isEdit || isNameChanged || areMembersChanged);

    const handleNameChange = (event) => {
        setName(event.target.value);
        setNameError("");
    };

    const handleSuccess = () =>
        succeed(
            language.getValue(
                isEdit ? "perimeters.toast.updated" : "perimeters.toast.created"
            )
        );

    const handleError = (err) => fail(err, { onNameError: setNameError });

    const handleSubmit = () => {
        if (!canSubmit || isSaving) return;
        setIsSaving(true);
        if (!isEdit) {
            ApiDatabase.postCreatePerimeter(
                { name: trimmedName, memberIds },
                handleSuccess,
                handleError
            );
            return;
        }
        // Only what changed is sent: an untouched name is not re-validated.
        const changes = { id: perimeter?.id };
        if (isNameChanged) changes.name = trimmedName;
        if (areMembersChanged) changes.memberIds = memberIds;
        ApiDatabase.patchPerimeter(changes, handleSuccess, handleError);
    };

    return (
        <>
            <Modalheader hide={close} hideShow={true}>
                {isEdit
                    ? fillText(language.getValue("perimeters.form.editTitle"), {
                          name: initial.name,
                      })
                    : language.getValue("perimeters.form.createTitle")}
            </Modalheader>
            <ModalBody>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-600">
                        {language.getValue(
                            isEdit
                                ? "perimeters.form.editDescription"
                                : "perimeters.form.createDescription"
                        )}
                    </p>
                    <div className="flex flex-col gap-1">
                        <label
                            htmlFor="perimeter-name"
                            className="text-sm text-gray-700"
                        >
                            {language.getValue("perimeters.form.nameLabel")}
                        </label>
                        <input
                            id="perimeter-name"
                            type="text"
                            autoFocus
                            value={name}
                            onChange={handleNameChange}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") handleSubmit();
                            }}
                            placeholder={language.getValue(
                                "perimeters.form.namePlaceholder"
                            )}
                            aria-invalid={displayedNameError ? "true" : "false"}
                            aria-describedby={
                                displayedNameError
                                    ? "perimeter-name-error"
                                    : undefined
                            }
                            className={`h-10 w-full rounded-md border px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none ${
                                displayedNameError
                                    ? "border-red-500"
                                    : "border-gray-300 focus:border-yellow-500"
                            }`}
                        />
                        {displayedNameError && (
                            <p
                                id="perimeter-name-error"
                                className="flex items-center gap-1 text-xs text-red-500"
                            >
                                <WarningCircle wh={16} color="#EF4444" />
                                {displayedNameError}
                            </p>
                        )}
                    </div>
                    <PerimeterMemberPicker
                        members={assignableMembers}
                        selectedIds={memberIds}
                        onChange={setMemberIds}
                    />
                </div>
            </ModalBody>
            <PerimeterModalFooter
                confirmLabel={language.getValue(
                    isEdit ? "perimeters.form.save" : "perimeters.form.create"
                )}
                savingLabel={language.getValue(
                    isEdit
                        ? "perimeters.form.saving"
                        : "perimeters.form.creating"
                )}
                isSaving={isSaving}
                canConfirm={canSubmit}
                onCancel={close}
                onConfirm={handleSubmit}
            />
        </>
    );
};

export default PerimeterFormModal;
